/**
 * Alert Repository
 *
 * Handles all alert-related data access with Zod schema validation.
 * Part of the Service/Repository pattern refactor (Chunk 2).
 *
 * @module services/repositories/AlertRepository
 */

import { z } from 'zod';
import { supabase } from '../supabase';
import { isSchemasCacheError } from '../supabaseErrors';
import { auditLogger } from '../auditLogger';
import type {
  Alert,
  RiskPattern,
  GlobalStats,
  ActivityLog,
  RiskCategory,
  ImpactLevel
} from '../../types';
import { OPEN_ALERT_STATUSES, fetchOpenAlertCounts } from '../metrics/openAlerts';

// ============================================
// ZOD SCHEMAS
// ============================================

/**
 * Helper for PostgreSQL NUMERIC/DECIMAL fields that return as strings
 */
const numericString = z.union([z.number(), z.string()]).nullable().transform(v =>
  v === null || v === undefined ? null : Number(v)
);

/**
 * Schema for alert status enum
 */
const AlertStatusSchema = z.enum([
  // v1
  'new', 'investigating', 'resolved', 'false_positive',
  // v2 (migration 20260422000003 alerts_status_check union)
  'acknowledged', 'in_progress', 'dismissed', 'escalated',
]);

/**
 * Schema for risk category enum
 */
const RiskCategorySchema = z.enum(['Operativo', 'Malicioso', 'Sistémico']);

/**
 * Schema for impact level enum
 */
const ImpactLevelSchema = z.enum(['Bajo', 'Medio', 'Alto']);

/**
 * Schema for raw alert record from database
 */
const AlertDbSchema = z.object({
  id: z.string(),
  tenant_id: z.string(),
  // alerts.store_id is nullable (tenant-wide KPI-rule alerts have no store)
  store_id: z.string().nullable(),
  type: z.string(),
  description: z.string().nullable(),
  details: z.string().nullable(),
  category: z.string().nullable(),
  potential_impact: z.string().nullable(),
  estimated_value: numericString,
  status: z.string().nullable(),
  assignee_id: z.string().nullable(),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable(),
  metadata: z.any().nullable(),
  // v2 columns (migration 20260422000003). Optional so partial selects /
  // pre-v2 rows still parse.
  rule_id: z.string().nullable().optional(),
  detection_id: z.string().nullable().optional(),
  due_at: z.string().nullable().optional(),
  resolution_root_cause: z.string().nullable().optional(),
  resolution_notes: z.string().nullable().optional(),
  escalation_count: z.number().nullable().optional(),
});

/**
 * Maps a validated alerts row to the frontend Alert model.
 * Shared by getAlerts / getAlertsByType so both expose the same fields.
 */
function mapDbAlert(validated: z.infer<typeof AlertDbSchema>): Alert {
  return {
    id: validated.id,
    timestamp: validated.created_at || new Date().toISOString(),
    type: validated.type,
    description: validated.description || '',
    details: validated.details || '',
    category: mapToRiskCategory(validated.category),
    potentialImpact: mapToImpactLevel(validated.potential_impact),
    estimatedValue: validated.estimated_value ?? undefined,
    status: mapToAlertStatus(validated.status),
    assignee: validated.assignee_id ?? undefined,
    storeId: validated.store_id ?? '',
    metadata: validated.metadata ?? undefined,
    rule_id: validated.rule_id ?? null,
    detection_id: validated.detection_id ?? null,
    due_at: validated.due_at ?? null,
    resolution_root_cause: validated.resolution_root_cause ?? null,
    resolution_notes: validated.resolution_notes ?? null,
    escalation_count: validated.escalation_count ?? null,
  };
}

/**
 * Schema for alert with store join
 */
const AlertWithStoreSchema = z.object({
  id: z.string(),
  type: z.string(),
  description: z.string().nullable(),
  status: z.string().nullable(),
  created_at: z.string().nullable(),
  stores: z.object({
    name: z.string(),
  }).nullable(),
});

/**
 * Schema for zone metrics with store_zones join
 */
const ZoneMetricDbSchema = z.object({
  zone_id: z.string(),
  risk_score: numericString,
  top_issue: z.string().nullable(),
  store_zones: z.object({
    name: z.string(),
  }).nullable(),
});

/**
 * Output schema for Alert type
 */
const AlertOutputSchema = z.object({
  id: z.string(),
  timestamp: z.string(),
  type: z.string(),
  description: z.string(),
  details: z.string(),
  category: RiskCategorySchema,
  potentialImpact: ImpactLevelSchema,
  estimatedValue: z.number().optional(),
  status: AlertStatusSchema,
  assignee: z.string().optional(),
  storeId: z.string(),
});

/**
 * Output schema for RiskPattern
 */
const RiskPatternSchema = z.object({
  areaName: z.string(),
  patternTitle: z.string(),
  patternDescription: z.string(),
  risk: z.number().min(0).max(1),
  recentAlerts: z.number(),
  estimatedLoss: z.number(),
  recommendedActions: z.array(z.string()),
});

/**
 * Output schema for ActivityLog
 */
const ActivityLogSchema = z.object({
  time: z.string(),
  event: z.string(),
  type: z.enum(['info', 'warning', 'alert']),
});

/**
 * Output schema for GlobalStats
 */
const GlobalStatsSchema = z.object({
  totalActiveAlerts: z.number(),
  avgGlobalRisk: z.number(),
  projectedLoss: z.number(),
  alertsTrend: z.array(z.number()),
  riskTrend: z.array(z.any()),
  topSegments: z.array(z.any()),
  recentGlobalActivity: z.array(ActivityLogSchema),
});

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Maps database category to RiskCategory enum
 */
function mapToRiskCategory(category: string | null): RiskCategory {
  const mapping: Record<string, RiskCategory> = {
    'operativo': 'Operativo' as RiskCategory,
    'malicioso': 'Malicioso' as RiskCategory,
    'sistemico': 'Sistémico' as RiskCategory,
    'Operativo': 'Operativo' as RiskCategory,
    'Malicioso': 'Malicioso' as RiskCategory,
    'Sistémico': 'Sistémico' as RiskCategory,
  };
  return mapping[category || ''] || ('Operativo' as RiskCategory);
}

/**
 * Maps database impact to ImpactLevel enum
 */
function mapToImpactLevel(impact: string | null): ImpactLevel {
  const mapping: Record<string, ImpactLevel> = {
    'bajo': 'Bajo' as ImpactLevel,
    'medio': 'Medio' as ImpactLevel,
    'alto': 'Alto' as ImpactLevel,
    'Bajo': 'Bajo' as ImpactLevel,
    'Medio': 'Medio' as ImpactLevel,
    'Alto': 'Alto' as ImpactLevel,
    'low': 'Bajo' as ImpactLevel,
    'medium': 'Medio' as ImpactLevel,
    'high': 'Alto' as ImpactLevel,
  };
  return mapping[impact || ''] || ('Medio' as ImpactLevel);
}

/**
 * Maps database status to Alert status type
 */
function mapToAlertStatus(status: string | null): Alert['status'] {
  const mapping: Record<string, Alert['status']> = {
    // v1
    'new': 'new',
    'investigating': 'investigating',
    'resolved': 'resolved',
    'false_positive': 'false_positive',
    'open': 'new',
    'closed': 'resolved',
    // v2 (migration 20260422000003)
    'acknowledged': 'acknowledged',
    'in_progress': 'in_progress',
    'dismissed': 'dismissed',
    'escalated': 'escalated',
  };
  return mapping[status || ''] || 'new';
}

// ============================================
// REPOSITORY IMPLEMENTATION
// ============================================

/**
 * AlertRepository handles all alert-related data access operations.
 * All return data is validated with Zod schemas for runtime type safety.
 */
export const AlertRepository = {
  /**
   * Get all alerts, filtered by tenant and optionally by store.
   * SECURITY: tenantId is required to prevent cross-tenant data leakage.
   * @param tenantId - Required tenant UUID for security filtering
   * @param storeId - Optional store UUID to filter by
   * @returns Array of validated Alert objects
   */
  async getAlerts(tenantId: string, storeId?: string): Promise<Alert[]> {
    // SECURITY: Require tenantId to prevent data leakage
    if (!tenantId) {
      console.error('[AlertRepository.getAlerts] tenantId is required for security');
      return [];
    }

    auditLogger.log({ tenantId, action: 'view', entityType: 'alerts', details: { storeId } });

    try {
      let query = supabase
        .from('alerts')
        .select('*')
        .eq('tenant_id', tenantId)  // SECURITY: Always filter by tenant
        .order('created_at', { ascending: false })
        .limit(500);

      if (storeId) {
        query = query.eq('store_id', storeId);
      }

      const { data, error } = await query;

      if (error) {
        if (isSchemasCacheError(error)) {
          console.debug('[AlertRepository] Alerts table not available (demo mode)');
        } else {
          console.error('[AlertRepository] Failed to fetch alerts:', error);
        }
        return [];
      }

      // Validate and transform each alert
      const alerts: Alert[] = (data || []).map(record => {
        try {
          return mapDbAlert(AlertDbSchema.parse(record));
        } catch (e) {
          console.warn('[AlertRepository] Failed to validate alert record:', e);
          return null;
        }
      }).filter((alert): alert is NonNullable<typeof alert> => alert !== null) as Alert[];

      return alerts;
    } catch (err) {
      console.error('[AlertRepository] Error fetching alerts:', err);
      return [];
    }
  },

  /**
   * Update an alert's status.
   * @param id - Alert UUID
   * @param status - New status
   * @param assignee - Optional assignee user id (public.users.id UUID).
   *   undefined/'' leaves assignee_id untouched; null clears it.
   */
  async updateAlertStatus(id: string, status: Alert['status'], assignee?: string | null, tenantId?: string): Promise<void> {
    // Throw, not return: a quiet return let callers report a save that never happened.
    if (!tenantId) {
      throw new Error('AlertRepository.updateAlertStatus: tenantId is required');
    }
    auditLogger.log({ tenantId, action: 'update', entityType: 'alerts', entityId: id, details: { status, assignee } });
    // Validate inputs
    const validatedId = z.string().uuid().parse(id);
    const validatedStatus = AlertStatusSchema.parse(status);

    const updates: Record<string, unknown> = { status: validatedStatus };
    if (assignee === null) {
      updates.assignee_id = null;
    } else if (assignee) {
      updates.assignee_id = z.string().uuid().parse(assignee);
    }

    const { error } = await supabase
      .from('alerts')
      .update(updates)
      .eq('id', validatedId)
      .eq('tenant_id', tenantId);

    if (error) {
      console.error('[AlertRepository] Failed to update alert status:', error);
      throw error;
    }
  },

  /**
   * Link an alert to a case. The relationship lives in the `case_alerts`
   * join table (PK case_id+alert_id) — `cases` has no related-alerts column.
   *
   * SECURITY: case_alerts has no tenant_id column, so tenant isolation is
   * enforced by first verifying that both the case and the alert belong to
   * tenantId (RLS policy case_alerts_insert is the backup). A duplicate link
   * (unique violation 23505) is treated as success.
   */
  async linkAlertToCase(alertId: string, caseId: string, tenantId: string): Promise<void> {
    if (!tenantId) {
      throw new Error('AlertRepository.linkAlertToCase: tenantId is required');
    }
    const validatedAlertId = z.string().uuid().parse(alertId);
    const validatedCaseId = z.string().uuid().parse(caseId);

    auditLogger.log({ tenantId, action: 'update', entityType: 'alerts', entityId: validatedAlertId, details: { linkedCaseId: validatedCaseId } });

    const [caseResult, alertResult] = await Promise.all([
      supabase
        .from('cases')
        .select('id')
        .eq('id', validatedCaseId)
        .eq('tenant_id', tenantId)
        .maybeSingle(),
      supabase
        .from('alerts')
        .select('id')
        .eq('id', validatedAlertId)
        .eq('tenant_id', tenantId)
        .maybeSingle(),
    ]);

    if (caseResult.error) throw caseResult.error;
    if (alertResult.error) throw alertResult.error;
    if (!caseResult.data || !alertResult.data) {
      throw new Error('AlertRepository.linkAlertToCase: case or alert not found for tenant');
    }

    // database.types.ts lists a case_alerts.tenant_id column, but the deployed
    // schema (supabase/schema/cloud_baseline.sql) only has (case_id, alert_id).
    const { error } = await supabase
      .from('case_alerts')
      .insert({ case_id: validatedCaseId, alert_id: validatedAlertId } as never);

    if (error && error.code !== '23505') {
      console.error('[AlertRepository.linkAlertToCase] INSERT failed:', error);
      throw error;
    }
  },

  /**
   * Get risk patterns for a store derived from alerts and zone metrics.
   * SECURITY: tenantId is required to prevent cross-tenant data leakage.
   * @param tenantId - Required tenant UUID for security filtering
   * @param storeId - The store UUID
   * @returns Array of validated RiskPattern objects
   */
  async getRiskPatterns(tenantId: string, storeId: string): Promise<RiskPattern[]> {
    // SECURITY: Require tenantId to prevent data leakage
    if (!tenantId) {
      console.error('[AlertRepository.getRiskPatterns] tenantId is required for security');
      return [];
    }

    auditLogger.log({ tenantId, action: 'view', entityType: 'risk_patterns', details: { storeId } });

    try {
      // Derive risk patterns from alerts and zone_metrics
      const { data: alerts, error: alertsError } = await supabase
        .from('alerts')
        .select('type, description, status, created_at, metadata, estimated_value')
        .eq('tenant_id', tenantId)  // SECURITY: Always filter by tenant
        .eq('store_id', storeId)
        .in('status', [...OPEN_ALERT_STATUSES])
        .order('created_at', { ascending: false })
        .limit(10);

      if (alertsError) {
        console.error(`[AlertRepository] Error fetching alerts (HTTP: ${alertsError.code}):`, alertsError.message);
        return [];
      }

      // Fetch zone metrics for additional context
      // SECURITY: Filter by tenant_id for defense-in-depth (RLS is backup)
      const { data: zoneMetrics } = await supabase
        .from('zone_metrics')
        .select('zone_id, risk_score, top_issue, store_zones(name)')
        .eq('tenant_id', tenantId)  // SECURITY: Always filter by tenant
        .eq('store_id', storeId)
        .order('risk_score', { ascending: false })
        .limit(5);

      // If no alerts, check if we have zone metrics to derive patterns from
      if ((!alerts || alerts.length === 0) && (!zoneMetrics || zoneMetrics.length === 0)) {
        console.debug(`[AlertRepository] No risk patterns found for store ${storeId}`);
        return [];
      }

      const patterns: RiskPattern[] = [];

      // Convert alerts to risk patterns
      if (alerts && alerts.length > 0) {
        for (const alert of alerts) {
          const metadata = alert.metadata as Record<string, unknown> | null;

          const pattern: RiskPattern = {
            areaName: (metadata?.zone as string) || 'General',
            patternTitle: alert.type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Patrón desconocido',
            patternDescription: alert.description || 'Patrón de riesgo detectado',
            risk: Math.min(1, Math.max(0, ((metadata?.risk_score as number) || 50) / 100)),
            recentAlerts: 1,
            estimatedLoss: Number(alert.estimated_value) || 0,
            recommendedActions: (metadata?.suggested_actions as string[]) || ['Review alert details', 'Investigate root cause']
          };

          // Validate pattern
          try {
            RiskPatternSchema.parse(pattern);
            patterns.push(pattern);
          } catch (e) {
            console.warn('[AlertRepository] Invalid risk pattern:', e);
          }
        }
      }

      // Add zone-based patterns if we have high-risk zones
      if (zoneMetrics && zoneMetrics.length > 0) {
        for (const metric of zoneMetrics) {
          try {
            const validated = ZoneMetricDbSchema.parse(metric);
            const zoneName = validated.store_zones?.name || 'Zona desconocida';
            const riskScore = Number(validated.risk_score) || 0;

            if (riskScore > 0.5 && !patterns.some(p => p.areaName === zoneName)) {
              const pattern: RiskPattern = {
                areaName: zoneName,
                patternTitle: validated.top_issue || 'Elevated Risk',
                patternDescription: `Zone ${zoneName} has elevated risk score of ${Math.round(riskScore * 100)}%`,
                risk: riskScore,
                recentAlerts: 0,
                estimatedLoss: 0,
                recommendedActions: ['Monitor zone activity', 'Check recent incidents']
              };

              RiskPatternSchema.parse(pattern);
              patterns.push(pattern);
            }
          } catch {
            // Skip invalid zone metrics
            continue;
          }
        }
      }

      return patterns;
    } catch (err) {
      console.error('[AlertRepository] Unexpected error:', err);
      return [];
    }
  },

  /**
   * Get global overview statistics including alert counts and recent activity.
   * SECURITY: Must filter by tenantId to prevent cross-tenant data leakage.
   * @param tenantId - The tenant UUID to filter by (required for security)
   * @returns GlobalStats with validated data
   */
  async getGlobalOverview(tenantId?: string): Promise<GlobalStats> {
    if (tenantId) {
      auditLogger.log({ tenantId, action: 'view', entityType: 'global_overview' });
    }
    try {
      // SECURITY: If no tenantId provided, return empty stats to prevent data leakage
      if (!tenantId) {
        console.warn('[AlertRepository.getGlobalOverview] No tenantId provided - returning empty stats');
        return {
          totalActiveAlerts: 0,
          avgGlobalRisk: 0,
          projectedLoss: 0,
          alertsTrend: [],
          riskTrend: [],
          topSegments: [],
          recentGlobalActivity: [],
        };
      }

      // Fetch multiple data points in parallel - FILTERED BY TENANT
      const [alertCounts, kpisResult, recentAlertsResult] = await Promise.all([
        fetchOpenAlertCounts(tenantId),
        supabase
          .from('dashboard_kpis')
          .select('kpi_type, value')
          .eq('tenant_id', tenantId)
          .is('store_id', null),
        supabase
          .from('alerts')
          .select('id, type, description, status, created_at, stores(name)')
          .eq('tenant_id', tenantId)
          .order('created_at', { ascending: false })
          .limit(5)
      ]);

      // Calculate projected loss from shrinkage KPIs
      const shrinkageKpis = (kpisResult.data || []).filter((k: any) => k.kpi_type === 'shrinkage');
      const projectedLoss = shrinkageKpis.reduce((sum: number, k: any) => sum + (Number(k.value) || 0), 0);

      // Transform and validate recent alerts to activity
      const recentGlobalActivity: ActivityLog[] = (recentAlertsResult.data || []).map((a: any) => {
        try {
          const validated = AlertWithStoreSchema.parse(a);
          const activity: ActivityLog = {
            time: validated.created_at ? new Date(validated.created_at).toLocaleTimeString() : '',
            event: validated.description || validated.type || 'Alert',
            type: 'info' as const
          };
          return ActivityLogSchema.parse(activity);
        } catch {
          return null;
        }
      }).filter((item): item is ActivityLog => item !== null);

      // Calculate avgGlobalRisk using severity-weighted formula:
      // Critical alerts (open + impacto Alto) weight 3x, normal alerts weight 1x.
      // Weighted score / 15 * 100, capped at 100.
      // Example: 3 critical + 2 normal = (3*3 + 2*1) = 11 -> 73% risk
      const totalAlerts = alertCounts.open;
      const criticalAlerts = alertCounts.critical;
      const normalAlerts = totalAlerts - criticalAlerts;
      const weightedScore = (criticalAlerts * 3) + (normalAlerts * 1);
      const calculatedRisk = weightedScore > 0 ? Math.min(100, Math.round((weightedScore / 15) * 100)) : 0;

      const result: GlobalStats = {
        totalActiveAlerts: totalAlerts,
        avgGlobalRisk: calculatedRisk,
        projectedLoss,
        alertsTrend: [],
        riskTrend: [],
        topSegments: [],
        recentGlobalActivity
      };

      // Validate final output
      GlobalStatsSchema.parse(result);
      return result;
    } catch (err) {
      console.error('[AlertRepository] Failed to fetch global overview:', err);
      return {
        totalActiveAlerts: 0,
        avgGlobalRisk: 0,
        projectedLoss: 0,
        alertsTrend: [],
        riskTrend: [],
        topSegments: [],
        recentGlobalActivity: [],
      };
    }
  },

  /**
   * Get count of active alerts for a tenant, optionally filtered by store.
   * SECURITY: tenantId is required to prevent cross-tenant data leakage.
   * @param tenantId - Required tenant UUID for security filtering
   * @param storeId - Optional store UUID
   * @returns Number of active alerts
   */
  async getActiveAlertCount(tenantId: string, storeId?: string): Promise<number> {
    // SECURITY: Require tenantId to prevent data leakage
    if (!tenantId) {
      console.error('[AlertRepository.getActiveAlertCount] tenantId is required for security');
      return 0;
    }

    auditLogger.log({ tenantId, action: 'view', entityType: 'alerts', details: { query: 'active_count', storeId } });

    try {
      let query = supabase
        .from('alerts')
        .select('id', { count: 'exact', head: true })
        .eq('tenant_id', tenantId)  // SECURITY: Always filter by tenant
        .in('status', [...OPEN_ALERT_STATUSES]);

      if (storeId) {
        query = query.eq('store_id', storeId);
      }

      const { count, error } = await query;

      if (error) {
        console.error('[AlertRepository] Failed to count active alerts:', error);
        return 0;
      }

      return z.number().parse(count || 0);
    } catch (err) {
      console.error('[AlertRepository] Error counting active alerts:', err);
      return 0;
    }
  },

  /**
   * Get alerts by type, filtered by tenant.
   * SECURITY: tenantId is required to prevent cross-tenant data leakage.
   * @param tenantId - Required tenant UUID for security filtering
   * @param type - Alert type to filter by
   * @param storeId - Optional store UUID
   * @returns Array of validated Alert objects
   */
  async getAlertsByType(tenantId: string, type: string, storeId?: string): Promise<Alert[]> {
    // SECURITY: Require tenantId to prevent data leakage
    if (!tenantId) {
      console.error('[AlertRepository.getAlertsByType] tenantId is required for security');
      return [];
    }

    auditLogger.log({ tenantId, action: 'view', entityType: 'alerts', details: { type, storeId } });

    try {
      // Validate input
      const validatedType = z.string().min(1).parse(type);

      let query = supabase
        .from('alerts')
        .select('*')
        .eq('tenant_id', tenantId)  // SECURITY: Always filter by tenant
        .eq('type', validatedType)
        .order('created_at', { ascending: false });

      if (storeId) {
        query = query.eq('store_id', z.string().uuid().parse(storeId));
      }

      const { data, error } = await query;

      if (error) {
        console.error('[AlertRepository] Failed to fetch alerts by type:', error);
        return [];
      }

      // Validate and transform
      return (data || []).map(record => {
        try {
          return mapDbAlert(AlertDbSchema.parse(record));
        } catch {
          return null;
        }
      }).filter((alert): alert is NonNullable<typeof alert> => alert !== null) as Alert[];
    } catch (err) {
      console.error('[AlertRepository] Error fetching alerts by type:', err);
      return [];
    }
  },

  // ============================================
  // v2 — Alert → Task closed loop (PR #3, blueprint §3.10)
  // ============================================

  /**
   * Resolve an alert with a structured root cause. Writes two rows in a single
   * RPC call (`resolve_alert_transactional`) to prevent partial-write states:
   *
   *   1. UPDATE alerts SET status='RESOLVED', resolution_root_cause=..., resolution_notes=...
   *   2. INSERT INTO task_events (event_type='RESOLUTION', payload={root_cause, notes})
   *
   * Falls back to two separate writes if the RPC is not yet deployed (PR #5
   * adds the server-side function).
   *
   * SECURITY: tenantId is required for tenant isolation.
   */
  async resolveAlert(
    alertId: string,
    tenantId: string,
    resolution: { root_cause_code: string; notes?: string; actor_id?: string },
  ): Promise<void> {
    if (!tenantId) {
      throw new Error('AlertRepository.resolveAlert: tenantId is required');
    }
    if (!resolution?.root_cause_code) {
      throw new Error('AlertRepository.resolveAlert: root_cause_code is required');
    }

    // Preferred path: atomic RPC (added in PR #5)
    const { error: rpcError } = await supabase.rpc('resolve_alert_transactional', {
      p_alert_id: alertId,
      p_tenant_id: tenantId,
      p_root_cause_code: resolution.root_cause_code,
      p_notes: resolution.notes ?? null,
      p_actor_id: resolution.actor_id ?? null,
    });

    if (!rpcError) return;

    // Fallback: two writes (non-atomic). Acceptable until PR #5 lands.
    if (rpcError.code !== '42883' /* function does not exist */) {
      console.error('[AlertRepository.resolveAlert] RPC failed:', rpcError);
      throw rpcError;
    }

    const nowIso = new Date().toISOString();

    const { error: updateError } = await supabase
      .from('alerts')
      .update({
        status: 'resolved',
        resolution_root_cause: resolution.root_cause_code,
        resolution_notes: resolution.notes ?? null,
        resolved_at: nowIso,
        updated_at: nowIso,
      })
      .eq('id', alertId)
      .eq('tenant_id', tenantId);

    if (updateError) {
      console.error('[AlertRepository.resolveAlert] UPDATE failed:', updateError);
      throw updateError;
    }

    const { error: eventError } = await supabase
      .from('task_events')
      .insert({
        alert_id: alertId,
        tenant_id: tenantId,
        event_type: 'RESOLUTION',
        actor_id: resolution.actor_id ?? null,
        actor_is_system: !resolution.actor_id,
        payload: {
          root_cause_code: resolution.root_cause_code,
          notes: resolution.notes ?? null,
        },
      });

    if (eventError) {
      console.error('[AlertRepository.resolveAlert] task_events INSERT failed:', eventError);
      throw eventError;
    }
  },

  /**
   * Create a follow-up task for an alert. Writes both the task (with
   * tasks.alert_id set, per migration 20260422000006) and a task_events
   * COMMENT row referencing the new task.
   *
   * SECURITY: tenantId is required for tenant isolation.
   */
  async createTaskFromAlert(
    alertId: string,
    tenantId: string,
    taskData: {
      title: string;
      description?: string;
      type?: string;
      priority?: 'low' | 'medium' | 'high' | 'critical';
      assigned_to?: string;
      due_date?: string;
      store_id?: string;
    },
  ): Promise<string> {
    if (!tenantId) {
      throw new Error('AlertRepository.createTaskFromAlert: tenantId is required');
    }

    // Dynamic import so the repository layer stays free of import cycles —
    // TaskRepository does not depend on AlertRepository, but we keep the
    // static graph small.
    const { TaskRepository } = await import('./TaskRepository');

    const newTaskId = crypto.randomUUID();
    await TaskRepository.createTask(
      {
        id: newTaskId,
        title: taskData.title,
        description: taskData.description ?? '',
        status: 'todo',
        priority: taskData.priority ?? 'medium',
        type: (taskData.type ?? 'detection_followup') as never,
        dueDate: taskData.due_date,
        assigneeId: taskData.assigned_to,
      } as never,
      tenantId,
      taskData.store_id,
      alertId,
    );

    const { error: eventError } = await supabase
      .from('task_events')
      .insert({
        alert_id: alertId,
        tenant_id: tenantId,
        event_type: 'COMMENT',
        actor_id: taskData.assigned_to ?? null,
        actor_is_system: false,
        payload: {
          kind: 'task_created',
          task_id: newTaskId,
          title: taskData.title,
        },
      });

    if (eventError) {
      console.error('[AlertRepository.createTaskFromAlert] task_events INSERT failed:', eventError);
      // Non-fatal: task exists even if audit event failed. Surface but don't throw.
    }

    return newTaskId;
  },
};

// Export schemas for external use
export {
  AlertDbSchema,
  AlertOutputSchema,
  RiskPatternSchema,
  GlobalStatsSchema,
  ActivityLogSchema,
  AlertStatusSchema,
  RiskCategorySchema,
  ImpactLevelSchema,
};
